import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import type { LineItemInput } from './quote.schema';
import { computeTotal, formatILS, quoteSchema } from './quote.schema';
import {
  useCreateQuote,
  useCustomersForQuote,
  useLeadsForQuote,
} from './quotesQueries';
import styles from './quotes.module.css';

interface Props {
  open: boolean;
  orgId: string | null;
  userId: string | null;
  /** When provided, the wizard pre-fills source_lead_id and skips the lead picker. */
  preselectedLead?: { id: string; full_name: string; company_name: string | null } | null;
  onClose: () => void;
  onCreated: (quoteId: string) => void;
}

interface ItemFormState extends LineItemInput {
  key: string;
}

function emptyItem(): ItemFormState {
  return {
    key: crypto.randomUUID(),
    service_label: '',
    description: '',
    quantity: 1,
    unit_price: 0,
  };
}

export function QuoteWizard({
  open,
  orgId,
  userId,
  preselectedLead,
  onClose,
  onCreated,
}: Props) {
  const toast = useToast();
  const { data: customers = [] } = useCustomersForQuote(orgId);
  const { data: leads = [] } = useLeadsForQuote(orgId);
  const createQuote = useCreateQuote(orgId, userId);

  const [customerId, setCustomerId] = useState<string>('');
  const [leadId, setLeadId] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [items, setItems] = useState<ItemFormState[]>([emptyItem()]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLeadId(preselectedLead?.id ?? '');
      setCustomerId('');
      setValidUntil('');
      setNotes('');
      setItems([emptyItem()]);
      setError(null);
    }
  }, [open, preselectedLead]);

  if (!open) return null;

  const total = computeTotal(items);

  function updateItem(index: number, patch: Partial<LineItemInput>) {
    setItems((current) => {
      const next = [...current];
      const target = next[index];
      if (!target) return current;
      next[index] = { ...target, ...patch };
      return next;
    });
  }

  function addItem() {
    setItems((current) => [...current, emptyItem()]);
  }

  function removeItem(key: string) {
    setItems((current) => (current.length > 1 ? current.filter((it) => it.key !== key) : current));
  }

  async function handleSave(status: 'draft' | 'sent') {
    setError(null);
    const payload = {
      customer_id: customerId === '' ? null : customerId,
      source_lead_id: leadId === '' ? null : leadId,
      valid_until: validUntil || null,
      notes: notes.trim() || undefined,
      status,
      items: items.map((it) => ({
        service_label: it.service_label,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
      })),
    };
    const parsed = quoteSchema.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(first?.message ?? 'נתונים לא תקינים');
      return;
    }
    try {
      const quoteId = await createQuote.mutateAsync(parsed.data);
      toast.success(status === 'sent' ? 'ההצעה נשמרה ונשלחה' : 'הטיוטה נשמרה');
      onCreated(quoteId);
    } catch {
      toast.error('לא הצלחנו לשמור · נסה שוב');
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        className={styles.section}
        style={{ maxWidth: 880, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className={styles.drawerHead}>
          <div>
            <h2 className={styles.drawerTitle}>הצעת מחיר חדשה</h2>
            <p className={styles.pageSub}>מספר ההצעה יוקצה אוטומטית כשתשמור.</p>
          </div>
          <button type="button" className={styles.btnGhost} onClick={onClose}>סגור</button>
        </div>

        <div className={styles.fieldGrid}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="quote-lead">ליד מקור</label>
            <select
              id="quote-lead"
              className={styles.select}
              value={leadId}
              disabled={Boolean(preselectedLead)}
              onChange={(e) => { setLeadId(e.target.value); }}
            >
              <option value="">— ללא ליד —</option>
              {preselectedLead ? (
                <option value={preselectedLead.id}>
                  {preselectedLead.full_name}
                  {preselectedLead.company_name ? ` · ${preselectedLead.company_name}` : ''}
                </option>
              ) : (
                leads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.full_name}{l.company_name ? ` · ${l.company_name}` : ''}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="quote-customer">לקוח</label>
            <select
              id="quote-customer"
              className={styles.select}
              value={customerId}
              onChange={(e) => { setCustomerId(e.target.value); }}
            >
              <option value="">— בחר לקוח (אופציונלי) —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="quote-valid">תוקף עד</label>
            <input
              id="quote-valid"
              type="date"
              className={styles.input}
              value={validUntil}
              onChange={(e) => { setValidUntil(e.target.value); }}
            />
          </div>
        </div>

        <div>
          <h3 className={styles.sectionTitle} style={{ marginBlockEnd: 12 }}>שורות</h3>
          <div className={styles.items}>
            <div className={styles.itemRowHead}>
              <span>שירות</span>
              <span>תיאור</span>
              <span>כמות</span>
              <span>מחיר יחידה</span>
              <span>סכום</span>
              <span></span>
            </div>
            {items.map((item, idx) => {
              const subtotal = item.quantity * item.unit_price;
              return (
                <div key={item.key} className={styles.itemRow}>
                  <input
                    className={styles.input}
                    placeholder="למשל: ניהול פרויקט"
                    value={item.service_label}
                    onChange={(e) => { updateItem(idx, { service_label: e.target.value }); }}
                  />
                  <input
                    className={styles.input}
                    placeholder="פירוט (אופציונלי)"
                    value={item.description ?? ''}
                    onChange={(e) => { updateItem(idx, { description: e.target.value }); }}
                  />
                  <input
                    className={styles.input}
                    type="number"
                    min={0}
                    step="0.5"
                    value={item.quantity}
                    onChange={(e) => { updateItem(idx, { quantity: Number(e.target.value) }); }}
                  />
                  <input
                    className={styles.input}
                    type="number"
                    min={0}
                    step="50"
                    value={item.unit_price}
                    onChange={(e) => { updateItem(idx, { unit_price: Number(e.target.value) }); }}
                  />
                  <span className={styles.subtotal}><bdi>{formatILS(subtotal)}</bdi></span>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => { removeItem(item.key); }}
                    aria-label="הסר שורה"
                    disabled={items.length === 1}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" className={styles.btnGhost} onClick={addItem}>
            <Plus size={14} aria-hidden="true" /> הוסף שורה
          </button>

          <div className={styles.totalRow}>
            <span>סה"כ:</span>
            <span className={styles.totalAmount}><bdi>{formatILS(total)}</bdi></span>
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="quote-notes">הערות ללקוח</label>
          <textarea
            id="quote-notes"
            className={styles.textarea}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); }}
            placeholder="תנאים, אופן תשלום, מועדי אספקה וכו'"
          />
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={cn(styles.actions, styles['actions--end'])}>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled={createQuote.isPending}
            onClick={() => { void handleSave('draft'); }}
          >
            שמור כטיוטה
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={createQuote.isPending}
            onClick={() => { void handleSave('sent'); }}
          >
            שמור ושלח
          </button>
        </div>
      </div>
    </div>
  );
}
